import React, { useEffect, useState, useMemo } from 'react';
import { fetchBugReports, updateBugReportStatus, deleteBugReport, saveBugSolution } from '../../services/cloudService';
import { suggestBugFix } from '../../services/geminiService';
import { BugReport } from '../../types';
import { Bug, Loader2, ChevronDown, ChevronRight, CheckCircle2, RotateCcw, Trash2, Sparkles } from 'lucide-react';

const SOURCE_META: Record<BugReport['source'], { label: string; classes: string }> = {
    boundary: { label: 'Rendering', classes: 'bg-red-100 text-red-700 border-red-200' },
    window: { label: 'Fehler', classes: 'bg-orange-100 text-orange-700 border-orange-200' },
    rejection: { label: 'Promise', classes: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const CREATE_TABLE_SQL = `CREATE TABLE bug_reports (
    id UUID DEFAULT uuid_generate_v4() PRIMARY KEY,
    created_at TIMESTAMPTZ DEFAULT NOW(),
    last_seen_at TIMESTAMPTZ DEFAULT NOW(),
    fingerprint TEXT NOT NULL UNIQUE,
    message TEXT NOT NULL,
    stack TEXT,
    component_stack TEXT,
    source TEXT NOT NULL CHECK (source IN ('boundary','window','rejection')),
    app_context TEXT,
    user_agent TEXT,
    user_id UUID REFERENCES auth.users(id),
    occurrence_count INTEGER DEFAULT 1,
    status TEXT DEFAULT 'open' CHECK (status IN ('open','resolved')),
    ai_solution TEXT,
    ai_solution_status TEXT DEFAULT 'pending' CHECK (ai_solution_status IN ('pending','done','failed'))
);

ALTER TABLE bug_reports ENABLE ROW LEVEL SECURITY;

CREATE POLICY "bug_reports_select_admin" ON bug_reports FOR SELECT TO authenticated
  USING (EXISTS (SELECT 1 FROM profiles p WHERE p.id = auth.uid() AND p.role IN ('admin','super_admin')));
CREATE POLICY "bug_reports_update_admin" ON bug_reports FOR UPDATE TO authenticated
  USING (EXISTS (SELECT 1 FROM profiles p WHERE p.id = auth.uid() AND p.role IN ('admin','super_admin')));
CREATE POLICY "bug_reports_delete_admin" ON bug_reports FOR DELETE TO authenticated
  USING (EXISTS (SELECT 1 FROM profiles p WHERE p.id = auth.uid() AND p.role IN ('admin','super_admin')));

CREATE OR REPLACE FUNCTION report_bug(
  p_fingerprint TEXT, p_message TEXT, p_stack TEXT, p_component_stack TEXT,
  p_source TEXT, p_app_context TEXT, p_user_agent TEXT
) RETURNS TABLE(report_id UUID, is_new BOOLEAN, has_solution BOOLEAN)
LANGUAGE plpgsql SECURITY DEFINER SET search_path = public AS $$
DECLARE v_id UUID; v_new BOOLEAN; v_has BOOLEAN;
BEGIN
  INSERT INTO bug_reports (fingerprint, message, stack, component_stack, source, app_context, user_agent, user_id)
  VALUES (left(p_fingerprint, 500), left(p_message, 2000), left(p_stack, 8000), left(p_component_stack, 8000),
          p_source, left(p_app_context, 200), left(p_user_agent, 300), auth.uid())
  ON CONFLICT (fingerprint) DO UPDATE
    SET occurrence_count = bug_reports.occurrence_count + 1,
        last_seen_at = NOW(),
        status = 'open'
  RETURNING id, (xmax = 0), (ai_solution IS NOT NULL) INTO v_id, v_new, v_has;
  RETURN QUERY SELECT v_id, v_new, v_has;
END; $$;
GRANT EXECUTE ON FUNCTION report_bug TO authenticated;

CREATE OR REPLACE FUNCTION set_bug_solution(p_id UUID, p_solution TEXT, p_status TEXT)
RETURNS VOID LANGUAGE plpgsql SECURITY DEFINER SET search_path = public AS $$
BEGIN
  UPDATE bug_reports SET ai_solution = p_solution,
    ai_solution_status = CASE WHEN p_status IN ('pending','done','failed') THEN p_status ELSE 'failed' END
  WHERE id = p_id;
END; $$;
GRANT EXECUTE ON FUNCTION set_bug_solution TO authenticated;

ALTER PUBLICATION supabase_realtime ADD TABLE bug_reports;`;

interface Props {
    onCountChanged?: () => void;
}

export const AdminBugReportsView: React.FC<Props> = ({ onCountChanged }) => {
    const [reports, setReports] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'open' | 'resolved' | 'all'>('open');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

    const load = async () => {
        const data = await fetchBugReports();
        setReports(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(
        () => filter === 'all' ? reports : reports.filter(r => r.status === filter),
        [reports, filter]
    );

    const handleStatusChange = async (report: BugReport, status: 'open' | 'resolved') => {
        try {
            await updateBugReportStatus(report.id, status);
            await load();
            onCountChanged?.();
        } catch (e) {
            console.error('Statuswechsel fehlgeschlagen:', e);
        }
    };

    const handleDelete = async (report: BugReport) => {
        if (!window.confirm('Bug-Report endgültig löschen?')) return;
        try {
            await deleteBugReport(report.id);
            await load();
            onCountChanged?.();
        } catch (e) {
            console.error('Löschen fehlgeschlagen:', e);
        }
    };

    const handleRegenerate = async (report: BugReport) => {
        setRegeneratingId(report.id);
        try {
            const solution = await suggestBugFix({
                message: report.message,
                stack: report.stack,
                componentStack: report.component_stack,
                source: report.source,
                appContext: report.app_context
            });
            await saveBugSolution(report.id, solution || '', solution ? 'done' : 'failed');
            await load();
        } finally {
            setRegeneratingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-4" />
                <p>Lade Bug-Reports...</p>
            </div>
        );
    }

    if (reports.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
                <Bug className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-sm font-bold text-slate-700 mb-1">Keine Bugs gemeldet</h3>
                <p className="text-xs">
                    Es wurden bisher keine Fehler automatisch erfasst.
                </p>
                <div className="mt-4 p-4 bg-amber-50 rounded-lg text-amber-800 text-xs text-left max-w-lg mx-auto border border-amber-200">
                    <strong className="block mb-1">Hinweis für den Administrator:</strong>
                    Damit dieses Feature funktioniert, muss folgende Tabelle in Supabase "SQL Editor" erstellt werden:
                    <pre className="mt-2 bg-white p-2 rounded border border-amber-200 text-[11px] overflow-auto max-h-64">
{CREATE_TABLE_SQL}
                    </pre>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                {([['open', 'Offen'], ['resolved', 'Gelöst'], ['all', 'Alle']] as const).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
                            filter === key
                            ? 'bg-white border-red-200 text-red-600 shadow-sm'
                            : 'bg-slate-50 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'
                        }`}
                    >
                        {label}
                    </button>
                ))}
                <span className="ml-auto text-[11px] font-mono text-slate-400">{filtered.length} Einträge</span>
            </div>

            {filtered.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
                    Keine Einträge in dieser Ansicht.
                </div>
            )}

            <div className="space-y-3">
                {filtered.map(report => {
                    const meta = SOURCE_META[report.source] || SOURCE_META.window;
                    const userName = report.profiles
                        ? `${report.profiles.first_name || ''} ${report.profiles.last_name || ''}`.trim() || report.profiles.email
                        : 'Unbekannt';
                    const isExpanded = expandedId === report.id;
                    return (
                        <div key={report.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <button
                                onClick={() => setExpandedId(isExpanded ? null : report.id)}
                                className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors"
                            >
                                {isExpanded
                                    ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                                    : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${meta.classes}`}>
                                    {meta.label}
                                </span>
                                <span className="text-sm font-semibold text-slate-800 truncate flex-1" title={report.message}>
                                    {report.message}
                                </span>
                                {report.occurrence_count > 1 && (
                                    <span className="text-[11px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full shrink-0">
                                        ×{report.occurrence_count}
                                    </span>
                                )}
                                <span className="text-[11px] text-slate-400 shrink-0 hidden sm:block">{userName}</span>
                                <span className="text-[11px] text-slate-400 shrink-0 hidden md:block">
                                    {new Date(report.last_seen_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                                </span>
                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${
                                    report.status === 'open'
                                    ? 'bg-red-50 text-red-600 border-red-200'
                                    : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                }`}>
                                    {report.status === 'open' ? 'Offen' : 'Gelöst'}
                                </span>
                            </button>

                            {isExpanded && (
                                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                                    <div className="text-[11px] text-slate-500">
                                        <span className="font-bold">Bereich:</span> {report.app_context || 'unbekannt'}
                                        <span className="mx-2 text-slate-300">|</span>
                                        <span className="font-bold">Zuerst:</span> {new Date(report.created_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                                    </div>

                                    {report.stack && (
                                        <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap text-slate-600">
                                            {report.stack}
                                        </pre>
                                    )}
                                    {report.component_stack && (
                                        <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap text-slate-600">
                                            {report.component_stack}
                                        </pre>
                                    )}

                                    {report.ai_solution ? (
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                            <div className="flex items-center gap-1.5 text-indigo-700 text-xs font-bold mb-1">
                                                <Sparkles className="w-3.5 h-3.5" /> KI-Lösungsvorschlag
                                            </div>
                                            <p className="text-xs text-indigo-900 whitespace-pre-line">{report.ai_solution}</p>
                                        </div>
                                    ) : report.ai_solution_status === 'pending' ? (
                                        <div className="flex items-center gap-2 text-xs text-slate-400">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lösungsvorschlag wird erstellt…
                                        </div>
                                    ) : (
                                        <div className="text-xs text-slate-400">Keine Lösung verfügbar.</div>
                                    )}

                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {report.status === 'open' ? (
                                            <button
                                                onClick={() => handleStatusChange(report, 'resolved')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Als gelöst markieren
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleStatusChange(report, 'open')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" /> Wieder öffnen
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleRegenerate(report)}
                                            disabled={regeneratingId === report.id}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                                        >
                                            {regeneratingId === report.id
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Sparkles className="w-3.5 h-3.5" />} Lösung neu generieren
                                        </button>
                                        <button
                                            onClick={() => handleDelete(report)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors ml-auto"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Löschen
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
